/**
 * Username Validation Utility
 *
 * Provides validation rules and functions for username format validation.
 * Requirements: 3.3, 3.4
 */

// Validation constants
export const VALIDATION_RULES = {
  minLength: 3,
  maxLength: 20,
  // Must start and end with alphanumeric, can contain alphanumeric, underscore, or hyphen
  pattern: /^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/,
  // Check for consecutive special characters
  noConsecutiveSpecial: /[_-]{2,}/,
  // Valid characters only
  validCharacters: /^[a-zA-Z0-9_-]+$/,
};

export const VALIDATION_MESSAGES = {
  tooShort: `Username must be at least ${VALIDATION_RULES.minLength} characters`,
  tooLong: `Username must be at most ${VALIDATION_RULES.maxLength} characters`,
  invalidCharacters:
    "Username can only contain letters, numbers, underscores, and hyphens",
  invalidStartEnd: "Username must start and end with a letter or number",
  consecutiveSpecial: "Username cannot contain consecutive special characters",
};

/**
 * Validates username format according to the rules
 * @param {string} username - The username to validate
 * @returns {Object} Validation result with isValid flag and error message
 */
export const validateUsernameFormat = (username) => {
  // Check if username is provided
  if (!username || typeof username !== "string") {
    return {
      isValid: false,
      error: "Username is required",
    };
  }

  const trimmedUsername = username.trim();

  // Check minimum length
  if (trimmedUsername.length < VALIDATION_RULES.minLength) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.tooShort,
    };
  }

  // Check maximum length
  if (trimmedUsername.length > VALIDATION_RULES.maxLength) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.tooLong,
    };
  }

  // Check for valid characters only
  if (!VALIDATION_RULES.validCharacters.test(trimmedUsername)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.invalidCharacters,
    };
  }

  // Check for consecutive special characters
  if (VALIDATION_RULES.noConsecutiveSpecial.test(trimmedUsername)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.consecutiveSpecial,
    };
  }

  // Check if starts and ends with alphanumeric
  if (!VALIDATION_RULES.pattern.test(trimmedUsername)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.invalidStartEnd,
    };
  }

  // All validations passed
  return {
    isValid: true,
    error: null,
  };
};

/**
 * Quick check if username format is valid (returns boolean only)
 * @param {string} username - The username to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidUsernameFormat = (username) => {
  return validateUsernameFormat(username).isValid;
};
